// User-facing file actions that go through the authenticated API client and
// surface failures via the global error toast — never a raw-JSON browser tab.

import { api } from './api';
import { notifyApiError } from './api-error';

function triggerBlobDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Open a file inline in a new tab for viewing (PDFs/images render in-browser).
 * Popup-blocker safe: the tab is opened synchronously on the click gesture, then
 * navigated once the content resolves.
 *
 * The file is ALWAYS fetched through the authenticated API and shown as a blob
 * URL. It used to prefer a presigned MinIO URL — but production presigns
 * against `MINIO_ENDPOINT=http://opus-minio:9000`, a hostname that only
 * resolves inside the server's Docker network, so every visitor got
 * ERR_NAME_NOT_RESOLVED (Nicole, Cheetah Ridge service agreement, 2026-08-18).
 * Any presigned URL handed to a browser has this flaw in this deployment —
 * the same lesson the blog's gated guides learned on 2026-08-12. Do not
 * reintroduce presigned URLs on browser-facing paths.
 */
export async function viewFileInNewTab(fileId: string): Promise<void> {
  // Reserve the tab during the user gesture so the async fetch doesn't trip
  // popup blockers. about:blank (not noopener) so we keep the handle to navigate.
  const win = window.open('about:blank', '_blank');
  try {
    const { blob } = await api.requestBlob(`/files/${fileId}`);
    const href = URL.createObjectURL(blob);
    if (win) {
      win.location.href = href;
    } else {
      // Popup blocked — navigate the current tab as a fallback.
      window.location.href = href;
    }
  } catch (error) {
    win?.close();
    notifyApiError(error);
  }
}

/**
 * Download a file to disk (authenticated). Used where a save makes more sense
 * than inline viewing (e.g. candidate CVs).
 */
export async function downloadFileById(fileId: string, fallbackName = 'download'): Promise<void> {
  try {
    const { blob, fileName } = await api.requestBlob(`/files/${fileId}`);
    triggerBlobDownload(blob, fileName || fallbackName);
  } catch (error) {
    notifyApiError(error);
  }
}

/**
 * Download an invoice PDF to disk (authenticated). Replaces the old
 * window.open(/api/invoices/:id/pdf), which failed with a raw UNAUTHORIZED blob.
 */
export async function downloadInvoicePdf(invoiceId: string, invoiceNumber?: string): Promise<void> {
  try {
    const { blob, fileName } = await api.requestBlob(`/invoices/${invoiceId}/pdf`);
    triggerBlobDownload(blob, fileName || `invoice-${invoiceNumber || invoiceId}.pdf`);
  } catch (error) {
    notifyApiError(error);
  }
}
